import { ApiError, CancelRequestError } from './error';

import { type IList, type IHttpDataService, type HttpRequestMethod } from './models';
import { AngularInjector } from '../angular';
import { type IRequestConfig } from 'angular';

export const MAX_PAGE_SIZE = 100;

export class A9HttpDataService implements IHttpDataService {
    private $http: angular.IHttpService;

    // @deprecated
    public get http(): angular.IHttpService {
        if (!this.$http) {
            this.$http = AngularInjector.get<angular.IHttpService>('$http');
        }
        return this.$http;
    }

    /**
     * Ensures any page size values in the params do not exceed `MAX_PAGE_SIZE`.
     *
     * Supports both pagination shapes:
     * - `params.page.size`
     * - `params.size`
     *
     * @param params - Request parameters that may contain pagination fields
     */
    private enforcePageSizeLimit(params?: any): void {
        if (!params) return;

        if (params.page?.size > MAX_PAGE_SIZE) {
            params.page.size = MAX_PAGE_SIZE;
        }

        if (params.size > MAX_PAGE_SIZE) {
            params.size = MAX_PAGE_SIZE;
        }
    }

    public getList<Response, Params extends { page?: number; size?: number }>(
        url: string,
        params: Params = null,
        cancel: Promise<void> = null
    ): Promise<IList<Response>> {
        this.enforcePageSizeLimit(params);

        const config: IRequestConfig = {
            method: 'GET',
            url: url,
            params: params,
            timeout: cancel
        };
        const result = this.http(config)
            .then((result: angular.IHttpResponse<any>) => {
                const list: IList<any> = {
                    items: [],
                    page: null
                };
                if (result.data.page) {
                    list.page = result.data.page;
                } else {
                    list.page = {
                        number: params?.page || 0,
                        size: params?.size || (result.data.content && result.data.content.length) || 0,
                        totalElements: (result.data.content && result.data.content.length) || 0,
                        totalPages: 1
                    };
                }
                if (result.data.content) {
                    list.items = result.data.content;
                }
                return list;
            })
            .catch((result: angular.IHttpResponse<any>) => {
                throw this.handleError(result, config);
            });

        return <Promise<IList<Response>>>result;
    }

    public getItem<Response, Params = unknown>(url: string, params: Params = null, cancel: Promise<void> = null): Promise<Response> {
        this.enforcePageSizeLimit(params);

        const config: IRequestConfig = {
            method: 'GET',
            url: url,
            params: params,
            timeout: cancel
        };
        const result = this.http<Response>(config)
            .then((result: angular.IHttpResponse<Response>) => result.data)
            .catch((e: angular.IHttpResponse<any>) => {
                throw this.handleError(e, config);
            });

        return <Promise<Response>>result;
    }

    public updateRequest<ReturnType, Data = any, Params = unknown>(
        url: string,
        method: string,
        data: Data = null,
        params: Params = null,
        cancel: Promise<void> = null
    ): Promise<ReturnType> {
        this.enforcePageSizeLimit(params);

        const config: IRequestConfig = {
            method: method,
            url: url,
            data: data || null,
            params: params || null,
            timeout: cancel
        };
        const result = this.http<ReturnType>(config)
            .then((result: angular.IHttpResponse<ReturnType>) => result.data)
            .catch((result: angular.IHttpResponse<ReturnType>) => {
                throw this.handleError(result, config);
            });

        return <Promise<ReturnType>>result;
    }

    public updateRequestFormData<Response, Params>(
        url: string,
        method: HttpRequestMethod,
        formData: FormData,
        params: Params = null,
        cancel: Promise<void> = null
    ): Promise<Response> {
        this.enforcePageSizeLimit(params);

        // for image uploading
        const config: IRequestConfig = {
            method: method,
            url: url,
            data: formData,
            transformRequest: angular.identity,
            headers: {
                'Content-Type': undefined
            },
            params: params,
            timeout: cancel
        };
        const result = this.http<Response>(config)
            .then((result: angular.IHttpResponse<Response>) => result.data)
            .catch((result: angular.IHttpResponse<Response>) => {
                throw this.handleError(result, config);
            });
        return <Promise<Response>>result;
    }

    public handleError(result: angular.IHttpResponse<any>, config: IRequestConfig): Error {
        if (result) {
            return new ApiError(result, config);
        }
        return new CancelRequestError();
    }

    public isError(error: ApiError, codes: string | string[]) {
        const result = error['result'];
        const check: string[] = Array.isArray(codes) ? codes : [codes];
        if (result && result.details && result.details.length) {
            for (let i = 0; i < result.details.length; i++) {
                const detail = result.details[i];
                if (check.indexOf(detail.code) !== -1) {
                    return true;
                }
            }
        } else if (result && result.code) {
            if (check.indexOf(result.code) !== -1) {
                return true;
            }
        }
        return result && check.indexOf(result.exception) !== -1;
    }

    public getErrorMessage(error: ApiError, code: string): string {
        const result = error['result'];
        const details = (result && result.details && result.details.find(item => item.code === code)) || null;
        return (details && (details.localizedMessage || details.message)) || undefined;
    }
}
